import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { focusElement } from 'platform/utilities/ui';

/**
 * Wraps the va-tabs web component: renders the tabs, reflects `activeTabId`, and
 * reports activations via `onTabChange`.
 *
 * va-tabs is uncontrolled after mount (no select API), so we remount it
 * (`key={activeTabId}`) to re-sync on no-click changes like back/forward. The
 * remount drops focus, so we restore it (focus effect).
 */
export default function TabbedDetail({
  label,
  tabs,
  activeTabId,
  onTabChange,
}) {
  const wrapperRef = useRef(null);
  // True between a user activating a tab and the resulting remount, so the focus
  // effect restores focus on activation.
  const userActivatedRef = useRef(false);
  // Whether focus was inside the tabs just before the current commit.
  const hadFocusRef = useRef(false);

  // AppealInfo redirects unknown ?tab= before render, so findIndex won't be -1;
  // Math.max(0, …) only guards a direct caller that skips that redirect.
  const activeIndex = Math.max(
    0,
    tabs.findIndex(tab => tab.id === activeTabId),
  );

  // On the persistent wrapper, not va-tabs, so the listener survives remounts.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    const handleSelected = event => {
      // va-tab-item is slotted into va-tabs' light DOM, so event.target is the
      // va-tab-item itself (no shadow retargeting).
      const nextId = event.target.getAttribute('target-id');
      if (nextId && nextId !== activeTabId) {
        userActivatedRef.current = true;
        onTabChange(nextId);
      }
    };
    wrapper.addEventListener('tabItemSelected', handleSelected);
    return () => wrapper.removeEventListener('tabItemSelected', handleSelected);
  }, [activeTabId, onTabChange]);

  // Read during render, before the remount destroys a focused tab; the browser
  // fires no reliable event for that removal (getSnapshotBeforeUpdate stand-in).
  if (wrapperRef.current) {
    hadFocusRef.current = wrapperRef.current.contains(document.activeElement);
  }

  // The remount drops focus and va-tabs never re-focuses; restore it after a
  // user activation or when the remount destroyed a focused tab, never steal it.
  useEffect(() => {
    if (!userActivatedRef.current && !hadFocusRef.current) return undefined;
    userActivatedRef.current = false;
    const selectedTab = wrapperRef.current?.querySelector(
      `va-tab-item[target-id="${activeTabId}"]`,
    );
    if (!selectedTab) return undefined;
    // focusElement waits for hydration and forces a tabindex so focus lands.
    focusElement(selectedTab);
    // va-tabs' late hydration re-slots the tab items, which can drop the
    // restored focus to <body> when the new panel content reflows; re-assert
    // once after the churn settles, never stealing focus the user moved.
    const recovery = setTimeout(() => {
      if (
        document.activeElement === document.body &&
        wrapperRef.current?.contains(selectedTab)
      ) {
        focusElement(selectedTab);
      }
    }, 300);
    return () => clearTimeout(recovery);
  }, [activeTabId]);

  // Numeric index; va-tabs parses initially-selected from the string attribute.
  return (
    <div ref={wrapperRef}>
      <va-tabs key={activeTabId} label={label} initially-selected={activeIndex}>
        {tabs.map(tab => (
          <React.Fragment key={tab.id}>
            <va-tab-item button-text={tab.label} target-id={tab.id} />
            <va-tab-panel panel-id={tab.id}>{tab.content}</va-tab-panel>
          </React.Fragment>
        ))}
      </va-tabs>
    </div>
  );
}

TabbedDetail.propTypes = {
  activeTabId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    }),
  ).isRequired,
  onTabChange: PropTypes.func.isRequired,
};
